import axiosInstance from '../../axiosInstance';
import { API_PAYMENT_STATISTICS, API_PLAYER_STATISTICS, API_VPN_STATISTICS, API_USER_STATISTICS, API_VPN_GET_PEER } from '../../config';
import { returnNationality } from '../../common/actions/returnNationality';
import { MONTH_KEYS, dayKeyToNumber, daysInMonth, rangeToIso } from './format';
import { parseProduct, productLabel, durationLabel, durationColor, isLegacyProduct, providerLabel, platformLabel } from './products';

/**
 * Chiamate al BE per le statistiche.
 * Ogni funzione restituisce i dati già normalizzati per i grafici, così i componenti non conoscono il formato del BE.
 * Il parametro signal permette di annullare la richiesta se i filtri cambiano prima della risposta.
 */

const toNumber = (value) => Number(value) || 0;

// oggetto { chiave: valore } => [{ key, label, value }] ordinato dal più grande
function toItems(object, labelFn = (key) => key, filterFn = () => true) {
    return Object.entries(object || {})
        .filter(([key]) => filterFn(key))
        .map(([key, value]) => ({ key, label: labelFn(key), value: toNumber(value) }))
        .sort((a, b) => b.value - a.value);
}

const isCountry = (code) => returnNationality(code) !== '';

function toCountryItems(object) {
    return toItems(object, returnNationality, isCountry).filter(item => item.value > 0);
}

function toGainsItems(object) {
    return toItems(object, productLabel, (code) => !isLegacyProduct(code)).filter(item => item.value !== 0);
}

/**
 * Righe del BE [{ id, products: [{ product, counter }] }] => serie per durata ({ key, label, color, values[] }),
 * solo per la famiglia di prodotto richiesta (vpnPass / vpnBox).
 */
function toDurationSeries(rows, size, indexOf, family) {
    const byMonths = new Map();

    (rows || []).forEach(row => {
        const index = indexOf(row.id);
        if (index === null || index === undefined || index < 0 || index >= size) return;

        (row.products || []).forEach(({ product, counter }) => {
            const parsed = parseProduct(product);
            if (parsed.family !== family || isLegacyProduct(product)) return;

            const months = parsed.duration ? parsed.duration.months : 0;
            if (!byMonths.has(months)) {
                byMonths.set(months, { key: String(months), label: durationLabel(parsed.duration) || productLabel(product), months, values: new Array(size).fill(0) });
            }
            byMonths.get(months).values[index] += toNumber(counter);
        });
    });

    return [...byMonths.values()]
        .sort((a, b) => a.months - b.months)
        .map((serie, i) => ({ ...serie, color: durationColor(serie.months, i) }));
}

const monthIndex = (key) => MONTH_KEYS.indexOf(key);
const dayIndex = (key) => {
    const day = dayKeyToNumber(key);
    return day === null ? null : day - 1;
};

export async function fetchUserStatistics(signal) {
    const { data } = await axiosInstance.get(API_USER_STATISTICS, { signal });

    return {
        total: toNumber(data.values && data.values.totalUser),
        confirmed: toNumber(data.values && data.values.signedUser)
    };
}

export async function fetchPeerCount(signal) {
    const { data } = await axiosInstance.get(API_VPN_GET_PEER, { params: { elementsByPage: 1, page: 1 }, signal });
    return toNumber(data.info && data.info.total);
}

export async function fetchPlayerStatistics({ year, month, from, to }, signal) {
    const range = rangeToIso(from, to);
    const { data } = await axiosInstance.get(API_PLAYER_STATISTICS, {
        params: {
            lifeTimeMonth: month,
            year,
            'filters[0][CreatedAtStart]': range.start,
            'filters[1][CreatedAtEnd]': range.end,
        },
        signal
    });

    const days = daysInMonth(year, month);

    // oggetto { aa: n, ab: n, ... } => array con un valore per ogni giorno del mese
    const toDays = (object) => {
        const values = new Array(days).fill(0);
        Object.entries(object || {}).forEach(([key, value]) => {
            const index = dayIndex(key);
            if (index !== null && index < values.length) values[index] = toNumber(value);
        });
        return values;
    };

    const toMonths = (object) => MONTH_KEYS.map(key => toNumber(object && object[key]));

    // dati divisi per tipo di licenza: i campi threeYears* sono delle licenze 3 anni, gli altri delle lifetime
    return {
        byDay: {
            threeYears: toDays(data.threeYearsActivatedByDay),
            lifetime: toDays(data.lifeTimeActivatedByDay)
        },
        byMonth: {
            threeYears: toMonths(data.threeYearsActivatedByMonth),
            lifetime: toMonths(data.lifeTimeActivatedByMonth)
        },
        platformsRange: {
            threeYears: toItems(data.threeYearsByPlatform, platformLabel),
            lifetime: toItems(data.licencesByPlatform, platformLabel)
        },
        platformsTotal: {
            threeYears: toItems(data.overAllThreeYearsByPlatform, platformLabel),
            lifetime: toItems(data.overAllLicencesByPlatform, platformLabel)
        },
        // overAll: Licences = licenze lifetime, ThreeYears = licenze 3 anni
        totalLifetime: toNumber(data.overAll && data.overAll.Licences),
        totalThreeYears: toNumber(data.overAll && data.overAll.ThreeYears),
        totalPlayers: toNumber(data.overAll && data.overAll.Players)
    };
}

export async function fetchPaymentStatistics({ year, month, day, from, to }, signal) {
    const range = rangeToIso(from, to);
    const { data } = await axiosInstance.get(API_PAYMENT_STATISTICS, {
        params: {
            monthUnPaired: month,
            monthPaired: month,
            // con "tutto il mese" il BE richiede comunque un giorno: i dati giornalieri in quel caso non vengono mostrati
            dayPaired: day || 1,
            year,
            'filters[0][IssuedAtStart]': range.start,
            'filters[1][IssuedAtEnd]': range.end,
        },
        signal
    });

    const countryRows = (data.vpnPassesByCountry || []).filter(row => isCountry(row.id));
    const countryIndex = new Map(countryRows.map((row, i) => [row.id, i]));
    const countrySeries = toDurationSeries(countryRows, countryRows.length, (id) => countryIndex.get(id), 'vpnPass');

    return {
        providers: toItems(data.providers, providerLabel),
        countriesMonth: toCountryItems(data.usersByMonths),
        countriesDay: toCountryItems(data.usersByDays),
        grossGains: toGainsItems(data.grossGains),
        netGains: toGainsItems(data.netGains),
        vpnByCountry: {
            series: countrySeries,
            items: countryRows
                .map((row, i) => ({
                    key: row.id,
                    label: returnNationality(row.id),
                    value: countrySeries.reduce((sum, serie) => sum + serie.values[i], 0),
                    segments: countrySeries.map(serie => ({ key: serie.key, value: serie.values[i] }))
                }))
                .filter(item => item.value > 0)
                .sort((a, b) => b.value - a.value)
        }
    };
}

export async function fetchVpnStatistics({ year, month }, signal) {
    const { data } = await axiosInstance.get(API_VPN_STATISTICS, { params: { month, year }, signal });
    const days = daysInMonth(year, month);

    return {
        byMonth: {
            vpnPass: toDurationSeries(data.vpnPassesByMonth, 12, monthIndex, 'vpnPass'),
            vpnBox: toDurationSeries(data.vpnPassesByMonth, 12, monthIndex, 'vpnBox')
        },
        byDay: {
            vpnPass: toDurationSeries(data.vpnPassesByDays, days, dayIndex, 'vpnPass'),
            vpnBox: toDurationSeries(data.vpnPassesByDays, days, dayIndex, 'vpnBox')
        },
        active: Object.entries(data.vpnPassesActiveTotal || {})
            .filter(([code]) => !isLegacyProduct(code))
            .map(([code, value]) => ({ key: code, label: productLabel(code), value: toNumber(value), parsed: parseProduct(code) }))
            .sort((a, b) => a.parsed.family.localeCompare(b.parsed.family) || ((a.parsed.duration ? a.parsed.duration.months : 0) - (b.parsed.duration ? b.parsed.duration.months : 0)))
            .map(({ parsed, ...item }) => item)
    };
}
